import {
  AcademicMajorWithRigor,
  TrackAssessmentResult,
  AcademicEnrollmentType,
  AcademicEnrollmentTypeCode,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

const ASSESS_MY_TRACK_STRINGS = appStrings.modules.assessMyTrack;

const GRADUATE_SCHOOL_ENROLLMENT_TYPE_CODE = 'graduate-school';

export const SELECTIVITY_ID_TO_DISPLAY_MAP: Record<number, string> = {
  1: ASSESS_MY_TRACK_STRINGS.collegeSelectivityLevels.lessSelective,
  2: ASSESS_MY_TRACK_STRINGS.collegeSelectivityLevels.somewhatSelective,
  3: ASSESS_MY_TRACK_STRINGS.collegeSelectivityLevels.highlySelective,
};

export const hasChartableAssessment = (history?: TrackAssessmentResult[]): boolean =>
  !!history?.some((result) => result.academicGrade && result.dimensionScores?.length);

const TENURE_MAP: Record<string, 'High School' | 'Post High School'> = {
  high_school: 'High School',
  post_secondary: 'Post High School',
};

/** The schooling stage a result was scored under, which selects its standards. */
export const getAssessmentTenure = (
  result: TrackAssessmentResult | null | undefined,
): 'High School' | 'Post High School' | undefined =>
  result?.enrollment_type_level ? TENURE_MAP[result.enrollment_type_level] : undefined;

export const getSchoolSelectivityLevel = (assessmentData: TrackAssessmentResult | null): string => {
  if (!assessmentData?.college?.selectivity) {
    return 'Less Selective';
  }

  return SELECTIVITY_ID_TO_DISPLAY_MAP[assessmentData.college.selectivity] || 'Less Selective';
};

const calculateMajorRigorScore = (major: AcademicMajorWithRigor): number => {
  const rigorAttributes = [
    'rigor_analytical_complexity',
    'rigor_technical_depth',
    'rigor_workload',
    'rigor_performance_standards',
    'rigor_competitiveness_attrition',
  ] as const;

  return rigorAttributes.reduce((total, attr) => total + (major[attr] || 2), 0);
};

/**
 * Calculate the major rigor level from individual rigor scores
 */
export const getMajorRigorLevel = (major: AcademicMajorWithRigor): string => {
  const rigorScore = calculateMajorRigorScore(major);

  if (rigorScore === 15) {
    return ASSESS_MY_TRACK_STRINGS.rigorLevels.veryRigorous;
  } else if (rigorScore >= 10) {
    return ASSESS_MY_TRACK_STRINGS.rigorLevels.rigorous;
  } else if (rigorScore >= 7) {
    return ASSESS_MY_TRACK_STRINGS.rigorLevels.lessRigorous;
  } else {
    return ASSESS_MY_TRACK_STRINGS.rigorLevels.veryLowRigor;
  }
};

export const getMostRigorousMajorLevel = (majors: AcademicMajorWithRigor[]): string => {
  if (!majors || majors.length === 0) {
    return ASSESS_MY_TRACK_STRINGS.rigorLevels.veryLowRigor;
  }

  const mostRigorousMajor = majors.reduce((mostRigorous, major) => {
    return calculateMajorRigorScore(major) > calculateMajorRigorScore(mostRigorous)
      ? major
      : mostRigorous;
  }, majors[0]);

  return getMajorRigorLevel(mostRigorousMajor);
};

export const isUnsupportedEnrollmentType = (
  academicEnrollmentTypes:
    | { level: AcademicEnrollmentType; code?: AcademicEnrollmentTypeCode }
    | undefined,
): boolean => {
  if (!academicEnrollmentTypes) return false;

  const { level, code } = academicEnrollmentTypes;

  if (level === 'none') return true;

  if (code === GRADUATE_SCHOOL_ENROLLMENT_TYPE_CODE) return true;

  return false;
};

const GRADE_LABELS = ASSESS_MY_TRACK_STRINGS.gradeLabels;

const GRADE_NAME_TO_LABEL: Record<string, string> = {
  '9th Grade': GRADE_LABELS.grade9,
  '10th Grade': GRADE_LABELS.grade10,
  '11th Grade': GRADE_LABELS.grade11,
  '12th Grade': GRADE_LABELS.grade12,
  '1st Year College': GRADE_LABELS.postSecondaryY1,
  '2nd Year College': GRADE_LABELS.postSecondaryY2,
  '3rd Year College': GRADE_LABELS.postSecondaryY3,
  '4th Year College': GRADE_LABELS.postSecondaryY4,
};

export const getGradeLabel = (gradeName: string): string =>
  GRADE_NAME_TO_LABEL[gradeName] || gradeName;

export const getGradeScorecardTitle = (gradeName: string): string =>
  `${getGradeLabel(gradeName)} ${ASSESS_MY_TRACK_STRINGS.scorecard}`;
